// src/data/progressStorage.js
import { supabase } from "../supabaseClient";
import { getSessionUser } from "../lib/sessionUser";

const PROGRESS_KEY_PREFIX = "fluent_progress_";

function getProgressKey(languageId, profileId) {
  const lang = languageId || "generic";
  const profile = profileId || "default";
  return `${PROGRESS_KEY_PREFIX}${profile}_${lang}`;
}

export function loadProgress(languageId, profileId) {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(
      getProgressKey(languageId, profileId)
    );
    if (!raw) return {};
    const data = JSON.parse(raw);
    if (data && typeof data === "object") return data;
    return {};
  } catch (e) {
    console.warn("Fluent! loadProgress error:", e);
    return {};
  }
}

function toIso(value) {
  if (value == null) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

function isMissingSrColumn(error) {
  return /sr_stage|sr_due_at/.test(error?.message || "");
}

/**
 * Lädt den Vokabel-Fortschritt aus Supabase (user_progress) im Format des lokalen Speichers.
 * Gibt null zurück, wenn das Laden fehlschlägt.
 */
export async function loadProgressRemote(userId, languageId, profileId) {
  const query = (columns) =>
    supabase
      .from("user_progress")
      .select(columns)
      .eq("user_id", userId)
      .eq("profile_id", profileId || "default")
      .eq("language_id", languageId || "generic");

  const base = "word_id, box, correct_streak, last_seen_at";
  let { data, error } = await query(`${base}, sr_stage, sr_due_at`);
  if (error && isMissingSrColumn(error)) {
    ({ data, error } = await query(base));
  }
  if (error) {
    console.warn("Fluent! loadProgressRemote error:", error);
    return null;
  }

  const progress = {};
  for (const row of data || []) {
    const lastSeen = row.last_seen_at ? Date.parse(row.last_seen_at) : null;
    const state = {
      box: row.box,
      correctStreak: row.correct_streak ?? 0,
      lastSeenAt: row.last_seen_at ?? null,
      lastReviewed: lastSeen,
    };
    if (row.sr_due_at) {
      state.srStage = row.sr_stage ?? 1;
      state.srDueAt = Date.parse(row.sr_due_at);
    }
    progress[row.word_id] = state;
  }
  return progress;
}

export function saveProgress(languageId, profileId, progress) {
  // 1) Lokal speichern
  if (typeof window !== "undefined") {
    try {
      window.localStorage.setItem(
        getProgressKey(languageId, profileId),
        JSON.stringify(progress || {})
      );
    } catch (e) {
      console.warn("Fluent! saveProgress local error:", e);
    }
  }

  // 2) Remote-Sync im Hintergrund
  (async () => {
    try {
      const { data: userData, error: userError } =
        await getSessionUser();
      if (userError || !userData?.user?.id) return;
      const userId = userData.user.id;
      const lang = languageId || "generic";
      const profile = profileId || "default";

      const rows = Object.entries(progress || {}).map(([wordId, state]) => ({
        user_id: userId,
        profile_id: profile,
        language_id: lang,
        word_id: wordId,
        box: state?.box ?? 0,
        correct_streak: state?.correctStreak ?? 0,
        last_seen_at: toIso(state?.lastReviewed ?? state?.lastSeenAt) ?? new Date().toISOString(),
        sr_stage: state?.srStage ?? null,
        sr_due_at: toIso(state?.srDueAt),
      }));

      if (rows.length === 0) return;

      const upsert = (r) =>
        supabase.from("user_progress").upsert(r, {
          onConflict: "user_id,profile_id,language_id,word_id",
        });

      let { error } = await upsert(rows);

      // Spalten sr_stage/sr_due_at fehlen noch in Supabase → ohne Wiederholplan speichern
      if (error && isMissingSrColumn(error)) {
        ({ error } = await upsert(
          rows.map(({ sr_stage, sr_due_at, ...rest }) => rest)
        ));
      }

      if (error) {
        console.warn("Fluent! saveProgress remote error:", error);
      }
    } catch (e) {
      console.warn("Fluent! saveProgress remote exception:", e);
    }
  })();
}

/**
 * Nur einzelne Wörter speichern (z. B. nach der Blitzabfrage): lokal in den gespeicherten
 * Fortschritt einfügen und remote nur diese Zeilen hochladen. Anders als saveProgress
 * überschreibt das keine anderen Wörter mit einem womöglich veralteten lokalen Stand.
 * @param {Object<string, object>} entries  word_id → state
 */
export function saveProgressEntries(languageId, profileId, entries) {
  const ids = Object.keys(entries || {});
  if (ids.length === 0) return;

  if (typeof window !== "undefined") {
    try {
      const local = loadProgress(languageId, profileId);
      window.localStorage.setItem(
        getProgressKey(languageId, profileId),
        JSON.stringify({ ...local, ...entries })
      );
    } catch (e) {
      console.warn("Fluent! saveProgressEntries local error:", e);
    }
  }

  (async () => {
    try {
      const { data: userData, error: userError } = await getSessionUser();
      if (userError || !userData?.user?.id) return;

      const rows = ids.map((wordId) => {
        const state = entries[wordId] || {};
        return {
          user_id: userData.user.id,
          profile_id: profileId || "default",
          language_id: languageId || "generic",
          word_id: wordId,
          box: state.box ?? 0,
          correct_streak: state.correctStreak ?? 0,
          last_seen_at: toIso(state.lastReviewed ?? state.lastSeenAt) ?? new Date().toISOString(),
          sr_stage: state.srStage ?? null,
          sr_due_at: toIso(state.srDueAt),
        };
      });

      const upsert = (r) =>
        supabase.from("user_progress").upsert(r, {
          onConflict: "user_id,profile_id,language_id,word_id",
        });

      let { error } = await upsert(rows);
      if (error && isMissingSrColumn(error)) {
        ({ error } = await upsert(rows.map(({ sr_stage, sr_due_at, ...rest }) => rest)));
      }
      if (error) console.warn("Fluent! saveProgressEntries remote error:", error);
    } catch (e) {
      console.warn("Fluent! saveProgressEntries remote exception:", e);
    }
  })();
}

export function resetProgress(languageId, profileId) {
  // lokal
  if (typeof window !== "undefined") {
    try {
      window.localStorage.removeItem(
        getProgressKey(languageId, profileId)
      );
    } catch (e) {
      console.warn("Fluent! resetProgress local error:", e);
    }
  }

  // remote
  (async () => {
    try {
      const { data: userData, error: userError } =
        await getSessionUser();
      if (userError || !userData?.user?.id) return;
      const userId = userData.user.id;
      const lang = languageId || "generic";
      const profile = profileId || "default";

      const { error } = await supabase
        .from("user_progress")
        .delete()
        .eq("user_id", userId)
        .eq("profile_id", profile)
        .eq("language_id", lang);

      if (error) {
        console.warn("Fluent! resetProgress remote error:", error);
      }
    } catch (e) {
      console.warn("Fluent! resetProgress remote exception:", e);
    }
  })();
}
